import * as Sentry from '@sentry/nextjs';

import { parseLogLevel } from '../config';

import type { LogLevel, LoggerPlugin, LogEntry } from '../types';

export const SENTRY_MIN_LEVEL: LogLevel = parseLogLevel(
  process.env.NEXT_PUBLIC_LOG_LEVEL_SENTRY,
  'error',
);

const removeASCII = (message: string) => {
  return message.replace(/\[.*?m|\x1B\[[0-?]*[ -/]*[@-~]/g, '');
};

// Normalize error messages into stable fingerprint keys so that errors
// sharing the same root cause (but differing only in URL or asset ID)
// are grouped into a single Sentry issue rather than creating one per URL.
//
// Returns [key, useStack]:
//   key      — the normalized fingerprint string
//   useStack — when false, context.stack is omitted from the fingerprint,
//              guaranteeing a single Sentry group regardless of stack variation
// Frames from the logger pipeline and bundler internals are not useful as the
// "source" of an error — skip them to surface the first app-level call site.
const SKIP_FRAME =
  /lib\/logger\/|intercept-console|node_modules|webpack-internal/;

// Parse the first app-level file + line from a stack string so it surfaces as
// explicit context in Sentry rather than requiring the reviewer to expand the
// full stacktrace panel.
const parseSourceFrame = (
  stack: string,
): { file: string; line: number; column: number } | null => {
  for (const frame of stack.split('\n').slice(1)) {
    const m =
      frame.match(/at .+? \((.+):(\d+):(\d+)\)/) ??
      frame.match(/at (.+):(\d+):(\d+)/);
    if (!m) continue;
    const [, file, line, col] = m;
    if (!file || SKIP_FRAME.test(file)) continue;
    return { file, line: Number(line), column: Number(col) };
  }
  return null;
};

const normalizeFingerprint = (
  message: string,
): [key: string, useStack: boolean] => {
  // Next.js image optimizer upstream 403s — each unique image URL would
  // otherwise create its own Sentry issue.
  if (/upstream image response failed/i.test(message)) {
    return ['upstream-image-response-failed', false];
  }

  // Next.js <Image> warnings — each unique src URL or filename would create its
  // own Sentry issue. All variants collapse to one group per warning type.
  if (/Image with src /i.test(message)) {
    const reason = message.includes('loader')
      ? 'nextjs-image-loader-warning'
      : 'nextjs-image-warning';
    return [reason, false];
  }

  // Mux player errors — group by type + details, ignore the stream URL.
  // Stack is excluded so errors thrown from different HLS.js call depths
  // still collapse into one Sentry issue.
  if (message.includes('getErrorFromHlsErrorData')) {
    const type = message.match(/"type"\s*:\s*"([^"]+)"/)?.[1] ?? 'unknown';
    const details =
      message.match(/"details"\s*:\s*"([^"]+)"/)?.[1] ?? 'unknown';
    return [`mux-${type}-${details}`, false];
  }

  // Default: strip any embedded URLs so per-endpoint noise is avoided,
  // and keep the stack so different call sites remain separate issues.
  return [message.replace(/https?:\/\/\S+/g, '[url]').slice(0, 200), true];
};

export const SentryPlugin: LoggerPlugin = {
  name: 'sentry',
  minLevel: SENTRY_MIN_LEVEL,

  async handle(entry: LogEntry) {
    const { level, message, context, tags, traceId } = entry;

    // map log-levels
    const sentryLevel =
      level === 'debug'
        ? 'debug'
        : level === 'info'
          ? 'info'
          : level === 'warn'
            ? 'warning'
            : 'error';

    Sentry.withScope(scope => {
      // attach context
      if (context) {
        Object.entries(context).forEach(([k, v]) =>
          scope.setContext(
            k,
            typeof v === 'object' && v !== null
              ? (v as Record<string, unknown>)
              : { value: v },
          ),
        );
      }

      // attach tags / traceId
      if (tags) scope.setTags(Object.fromEntries(tags.map(t => [t, true])));
      if (traceId) scope.setTag('traceId', traceId);

      scope.setLevel(sentryLevel);
      const cleanedMessage = removeASCII(message);
      const [fpKey, useStack] = normalizeFingerprint(cleanedMessage);
      scope.setFingerprint([
        fpKey,
        useStack ? String(context?.stack || '') : '',
      ]);

      if (context?.stack) {
        const source = parseSourceFrame(String(context.stack));
        if (source) scope.setContext('source', source);
        const err = new Error(cleanedMessage);
        err.stack = String(context.stack);
        Sentry.captureException(err);
      } else {
        Sentry.captureMessage(cleanedMessage);
      }
    });
  },

  flush: () => Sentry.flush(2000).then(() => void 0),
  setUser: user => Sentry.setUser(user),
};
